import { useEffect, useMemo, useState } from 'react';
import { ApiError, getBallot, getCurrentRound, getTracks, putBallot } from '../core/api';
import {
  draftFromEntries,
  draftToEntries,
  guessesComplete,
  isSubmittable,
  pointsComplete,
  vetoCount,
  type Dim,
  type Draft,
} from '../core/ballot';
import { useAsync } from '../core/useAsync';
import type { Ballot, CurrentRound, Ruleset, Track } from '../core/types';

interface Loaded {
  round: CurrentRound | null;
  tracks: Track[];
  ballot: Ballot | null;
}

export interface SaveResult {
  submitted: boolean;
  error?: string;
}

/**
 * The engine behind Vote / Veto / Guess. Loads the current round, its tracks and
 * the caller's ballot draft, and owns the merged draft the three screens each
 * edit one dimension of. Saving assembles the whole ballot and auto-finalises
 * (submit:true) once Vote+Veto+Guess are all complete.
 *
 * The caller's own track is identified by the server's `is_mine` flag (backend
 * PR #5) and excluded from the ballot (rule 1's "none for your own"); the economy
 * (budget/range/multiplier/veto_penalty) comes live off the round's ruleset — no
 * hardcoded numbers, no client-side guessing of ownership.
 */
export function useVoting() {
  const load = useAsync<Loaded>(async () => {
    const round = await getCurrentRound();
    if (!round || round.phase !== 'VOTING') return { round, tracks: [], ballot: null };
    const [tr, ballot] = await Promise.all([getTracks(round.round_id), getBallot(round.round_id)]);
    return { round, tracks: tr.tracks, ballot };
  }, []);

  const round = load.data?.round ?? null;
  const tracks = load.data?.tracks ?? [];
  const ballot = load.data?.ballot ?? null;

  // Own track straight from the server flag; everything else is votable.
  const votable = useMemo(() => tracks.filter((t) => !t.is_mine), [tracks]);

  const [draft, setDraft] = useState<Draft>(new Map());
  useEffect(() => {
    if (!ballot) return;
    setDraft(draftFromEntries(ballot.entries, votable.map((t) => t.submission_id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load.data]);

  // The economy is the round's ruleset (rounds/current), falling back to the
  // ballot's copy — both carry the same fields.
  const eco: Ruleset =
    round?.ruleset ??
    ballot?.ruleset ?? {
      budget: 0,
      track_min: 0,
      track_max: 0,
      negative_multiplier: 1,
      veto_penalty: 0,
      popularity_bonus: 0,
      popularity_tie_bonus: 0,
    };

  function update(submissionId: number, patch: Partial<Dim>) {
    setDraft((prev) => {
      const next = new Map(prev);
      const cur = next.get(submissionId) ?? { points: 0, guessed_user_id: null, is_veto: false };
      next.set(submissionId, { ...cur, ...patch });
      return next;
    });
  }

  /** Set exactly one veto (clears any other). */
  function setVeto(submissionId: number) {
    setDraft((prev) => {
      const next = new Map(prev);
      for (const [id, dim] of next) next.set(id, { ...dim, is_veto: id === submissionId });
      return next;
    });
  }

  async function save(): Promise<SaveResult> {
    if (!round) return { submitted: false, error: 'No round.' };
    const entries = draftToEntries(draft);
    const submit = isSubmittable(draft, eco);
    try {
      await putBallot(round.round_id, entries, submit);
      return { submitted: submit };
    } catch (e) {
      return { submitted: false, error: (e as ApiError).message || 'Could not save your ballot.' };
    }
  }

  /** Abstain from the veto: clear any pick and save (submits if the rest is done). */
  async function saveAbstain(): Promise<SaveResult> {
    if (!round) return { submitted: false, error: 'No round.' };
    const cleared: Draft = new Map([...draft].map(([id, d]) => [id, { ...d, is_veto: false }]));
    setDraft(cleared);
    const entries = draftToEntries(cleared);
    const submit = isSubmittable(cleared, eco);
    try {
      await putBallot(round.round_id, entries, submit);
      return { submitted: submit };
    } catch (e) {
      return { submitted: false, error: (e as ApiError).message || 'Could not save your ballot.' };
    }
  }

  return {
    loading: load.loading,
    error: load.error,
    round,
    tracks,
    votable,
    eco,
    draft,
    update,
    setVeto,
    save,
    saveAbstain,
    submittable: isSubmittable(draft, eco),
    // What still stands between the draft and a locked-in ballot (all three parts).
    remaining: {
      points: !pointsComplete(draft, eco),
      veto: vetoCount(draft) !== 1,
      guesses: !guessesComplete(draft),
    },
  };
}
